import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommonInput from '@src/new-components/Input/CommonInput';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import { Box } from '@mui/material';
import { validateFileSize } from '@src/utils/checkValid';
import { MAX_SIZE_IDENTITY_UPLOAD } from '@src/constants/withdrawal';
import PhoneInput from '@src/new-components/Input/PhoneInput';
import { useSelector } from 'react-redux';
import useLocation from '@src/hooks/useLocation';
import apis from '@src/apis';
import {
  convertPhoneCountryNumber,
  formatPhoneNumberWithDialCode,
} from '@src/services/phoneNumber';
import DatePickerCustom from '@src/new-components/DatePicker';
import { StyledPersonalInfo } from './index.style';
import IdentityUpload from './IdentityUpload';
import DigitalSignature from './DigitalSignature';

const PersonalInfo = ({
  personalInfo,
  errors,
  handleChangeText,
  disabled,
  setFieldError,
  touched,
}) => {
  const { t } = useTranslation();
  const {
    fullName,
    phoneNumber,
    email,
    address,
    identityNumber,
    taxCode,
    dateOfIssue,
    placeOfIssue,
  } = personalInfo || {};
  const personalInfoRef = useRef(personalInfo);
  const errorsRef = useRef(errors);
  const [countryCode, setCountryCode] = useState([]);
  const { accessToken } = useSelector((state) => state.auth);
  const locationState = useLocation();
  const [currentOption, setCurrentOption] = useState();

  useEffect(() => {
    personalInfoRef.current = personalInfo;
  }, [personalInfo]);

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  const defaultCodeCountry = useMemo(() => {
    const selectCountry = countryCode?.find(
      (country) => country?.code === locationState?.country,
    );
    return selectCountry;
  }, [locationState, countryCode]);

  const fetCodeCountry = async () => {
    const data = await apis.country.getAllCountry(accessToken);
    setCountryCode(data || []);
  };

  useEffect(() => {
    fetCodeCountry();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    handleChangeText(value)(name);
    if (name === 'fullName') {
      const normalizeValue = value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .toUpperCase()
        .trim();
      handleChangeText(normalizeValue)('accountOwner');
    }
    // Only allow number and max length 12
    if (name === 'identityNumber') {
      const normalizeValue = value.replace(/\D/g, '').slice(0, 12);
      handleChangeText(normalizeValue)(name);
    }
  };

  const renderLabelInput = (label) => (
    <Typography variant="body-14-medium" color={COLOR.darkGray}>
      {label}
    </Typography>
  );

  const verifyCountryPhoneNumber = () =>
    convertPhoneCountryNumber(phoneNumber, currentOption?.dialCode);

  const handleBlurPhoneCountryNumber = () => {
    if (verifyCountryPhoneNumber()) {
      handleChangeText(
        formatPhoneNumberWithDialCode(currentOption?.dialCode, phoneNumber),
      )('phoneNumber');
    }
  };
  const handleValidateFile = (key, file) => {
    const newErrors = {};
    const isValidSize = validateFileSize(file.size, MAX_SIZE_IDENTITY_UPLOAD);
    if (!isValidSize) newErrors[key] = 'fileMaxSizeInValid';
    setFieldError(key, newErrors[key]);
    return Object.keys(newErrors).length > 0;
  };

  const handleDrop = (key) => (file) => {
    const hasError = handleValidateFile(key, file);
    if (hasError) return;
    handleChangeText(file)(key);
    handleChangeText(URL.createObjectURL(file))(`${key}Preview`);
  };

  const handleSignatureChange = (signatureData) => {
    setFieldError('digitalSignature', undefined);
    handleChangeText(signatureData)('digitalSignature');
  };

  const handleChangeCountryCode = (option) => {
    setCurrentOption(option);
    handleChangeText(option?.dialCode)('countryCode');
  };

  return (
    <StyledPersonalInfo>
      <Typography variant="body-16-medium" color={COLOR.darkGray}>
        {t('personalInfo')}
      </Typography>
      <Box className="personal-info-input">
        <CommonInput
          name="fullName"
          label={renderLabelInput(t('fullNameV2'))}
          placeholder={t('fullNamePlaceholder')}
          value={fullName}
          disabled={disabled}
          onChange={handleChange}
          className="personal-info-input-item"
          error={!!errors?.fullName && touched?.fullName}
          helperText={t(errors?.fullName)}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '44px',
            },
            '& .MuiFormHelperText-root': {
              marginLeft: 0,
              marginTop: '4px',
            },
          }}
        />
        <PhoneInput
          name="phoneNumber"
          label={renderLabelInput(t('phoneNumber'))}
          placeholder={t('phoneNumberPlaceholder')}
          value={phoneNumber}
          disabled={disabled}
          onChange={handleChange}
          type="text"
          className="personal-info-input-item"
          error={!!errors?.phoneNumber && touched?.phoneNumber}
          helperText={t(errors?.phoneNumber)}
          defaultCodeCountry={defaultCodeCountry}
          countryCodes={countryCode}
          showCountryCode={!disabled}
          onBlur={handleBlurPhoneCountryNumber}
          currentOption={currentOption}
          setCurrentOption={handleChangeCountryCode}
        />
      </Box>
      <Box className="personal-info-input">
        <CommonInput
          name="email"
          label={renderLabelInput(t('email'))}
          placeholder={t('emailPlaceholder')}
          value={email}
          disabled
          onChange={handleChange}
          className="personal-info-input-item"
          error={!!errors?.email && touched?.email}
          helperText={t(errors?.email)}
        />
        <CommonInput
          name="address"
          label={renderLabelInput(t('address'))}
          placeholder={t('addressPlaceholder')}
          value={address}
          disabled={disabled}
          onChange={handleChange}
          className="personal-info-input-item"
          error={!!errors?.address && touched?.address}
          helperText={t(errors?.address)}
        />
      </Box>
      <Box className="personal-info-input">
        <CommonInput
          name="taxCode"
          label={renderLabelInput(t('taxId'))}
          placeholder={t('enterTaxId')}
          value={taxCode}
          disabled={disabled}
          onChange={handleChange}
          className="personal-info-input-item"
          error={!!errors?.taxCode && touched?.taxCode}
          helperText={t(errors?.taxCode)}
        />
        <CommonInput
          name="identityNumber"
          label={renderLabelInput(t('identity'))}
          placeholder={t('enterIdentity')}
          type="text"
          value={identityNumber}
          disabled={disabled}
          onChange={handleChange}
          className="personal-info-input-item"
          error={!!errors?.identityNumber && touched?.identityNumber}
          helperText={t(errors?.identityNumber)}
        />
      </Box>
      <Box className="personal-info-input">
        {disabled ? (
          <CommonInput
            name="dateOfIssue"
            label={renderLabelInput(t('dateOfIssue'))}
            value={dateOfIssue}
            disabled={disabled}
            className="personal-info-input-item"
          />
        ) : (
          <DatePickerCustom
            name="dateOfIssue"
            label={renderLabelInput(t('dateOfIssue'))}
            value={dateOfIssue}
            disabled={disabled}
            onChange={handleChange}
            className="personal-info-input-item"
            error={!!errors?.dateOfIssue && touched?.dateOfIssue}
            helperText={t(errors?.dateOfIssue)}
            maxDate={new Date()}
          />
        )}
        <CommonInput
          name="placeOfIssue"
          label={renderLabelInput(t('placeOfIssue'))}
          placeholder={t('placeOfIssuePlaceholder')}
          value={placeOfIssue}
          disabled={disabled}
          onChange={handleChange}
          className="personal-info-input-item"
          error={!!errors?.placeOfIssue && touched?.placeOfIssue}
          helperText={t(errors?.placeOfIssue)}
        />
      </Box>
      <Box className="personal-info-input">
        <IdentityUpload
          name="identityFront"
          label={renderLabelInput(t('identityPhotoFront'))}
          error={
            !!errors?.identityFrontImageUrl && touched?.identityFrontImageUrl
          }
          helperText={t(errors?.identityFrontImageUrl)}
          onDrop={handleDrop('identityFrontImageUrl')}
          file={personalInfo?.identityFrontImageUrlPreview}
          disabled={disabled}
        />
        <IdentityUpload
          name="identityBack"
          label={renderLabelInput(t('identityPhotoBack'))}
          error={
            !!errors?.identityBackImageUrl && touched?.identityBackImageUrl
          }
          helperText={t(errors?.identityBackImageUrl)}
          onDrop={handleDrop('identityBackImageUrl')}
          file={personalInfo?.identityBackImageUrlPreview}
          disabled={disabled}
        />
      </Box>
      <DigitalSignature
        error={!!errors?.digitalSignature && touched?.digitalSignature}
        helperText={t(errors?.digitalSignature)}
        onSignatureChange={handleSignatureChange}
        disabled={disabled}
      />
    </StyledPersonalInfo>
  );
};

export default PersonalInfo;
